import { useEffect, useState } from "react";

import {
    useAnalytics,
    useAnalyticsAttributes,
    useEnterprisePricingCalculator
} from "../../../components";
import { UiPricingPlanModel } from "../../../models";
import { EnterprisePricingTier } from "../../../utils/enterprise-pricing-calculator";
import { InfoCircle } from "../../UiIcon/basic";
import {
    Description,
    Item,
    Price,
    PriceAppend,
    PriceDetail,
    PriceMax,
    UserInputWrapper
} from "../UiPricingGrid.styles";

export type UiPricingPlanProps = {
    model: UiPricingPlanModel;
    index: number;
    planCount: number;
    pricing: EnterprisePricingTier[];
};

const TieredPlan = ({
    model,
    index,
    planCount,
    pricing
}: UiPricingPlanProps) => {
    const {
        highlight,
        description,
        beforeUserField,
        afterUserField,
        userFieldLabel,
        beforePrice,
        afterPrice,
        beforeSmallPrice,
        afterSmallPrice,
        beforeUserMax,
        afterUserMax
    } = model.content;

    const minUsers = 50;
    const maxUsers = 5000;
    const calculator = useEnterprisePricingCalculator(
        pricing,
        minUsers,
        maxUsers
    );
    const [users, setUsers] = useState(minUsers);
    const [price, setPrice] = useState(
        calculator.getPricesByUserCount(minUsers)[1].monthlyPricePerUser
    );
    const [smallPrice, setSmallPrice] = useState(
        calculator.getPricesByUserCount(minUsers)[1].annualPricePerUser
    );
    const [usedInput, setUsedInput] = useState(false);
    const [usedSlider, setUsedSlider] = useState(false);
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    useEffect(() => {
        setPrice(calculator.getPricesByUserCount(users)[1].monthlyPricePerUser);
        setSmallPrice(
            calculator.getPricesByUserCount(users)[1].annualPricePerUser
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: add missing dependencies
    }, [users]);

    const updateUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUsers(Math.min(maxUsers, parseInt(e.target.value || "0")));

        if (e.target.type === "range" && !usedSlider) {
            sendTrackEvent("usedSliderInput", {
                ...analyticsAttributes,
                event: "used",
                eventComponent: "slider",
                label: "Slided pricing calculator",
                labelEnglish: "Slided pricing calculator",
                uuid: `${model.id}_slider`
            });
            setUsedSlider(true);
        }
        if (e.target.type === "number" && !usedInput) {
            sendTrackEvent("usedNumberInput", {
                ...analyticsAttributes,
                event: "used",
                eventComponent: "numberInput",
                label: "Inputted number in pricing calculator",
                labelEnglish: "Inputted number in pricing calculator",
                uuid: `${model.id}_numberInput`
            });
            setUsedInput(true);
        }
    };

    return (
        <>
            <Item
                show={users >= minUsers}
                lgOrder={index + planCount * 1}
                highlight={highlight}
                first={index === 0}
            >
                <Price>
                    <PriceAppend>{beforePrice}</PriceAppend>
                    {price}
                    <PriceAppend>{afterPrice}</PriceAppend>
                </Price>
                <PriceDetail>
                    {[
                        beforeSmallPrice,
                        "$",
                        smallPrice,
                        " ",
                        afterSmallPrice
                    ].join("")}
                </PriceDetail>
            </Item>
            <Item
                lgOrder={index + planCount * 2}
                highlight={highlight}
                first={index === 0}
                show={true}
            >
                <Description>{description}</Description>
                <UserInputWrapper>
                    <div>
                        {beforeUserField}
                        <input
                            type="number"
                            value={users || ""}
                            min={minUsers}
                            max={maxUsers}
                            onChange={updateUsers}
                            aria-label={userFieldLabel}
                            onBlur={(e) => {
                                //only need to enforce min here. max is enforced by updateUsers
                                if (
                                    parseInt(e.target.value || "0") < minUsers
                                ) {
                                    setUsers(minUsers);
                                }
                            }}
                            onKeyDown={(e) =>
                                ["e", "E", "+", "-", "."].includes(e.key) &&
                                e.preventDefault()
                            }
                        />
                        {afterUserField}
                    </div>
                    <input
                        type="range"
                        value={users}
                        min={minUsers}
                        max={maxUsers}
                        onChange={updateUsers}
                        aria-label={userFieldLabel}
                    />
                    {users === maxUsers && (
                        <PriceMax>
                            <InfoCircle />
                            <p>
                                {[
                                    beforeUserMax,
                                    maxUsers.toLocaleString(),
                                    afterUserMax
                                ].join(" ")}
                            </p>
                        </PriceMax>
                    )}
                </UserInputWrapper>
            </Item>
        </>
    );
};

export default TieredPlan;
